// ---------------------------------------------------------------------------
// Explorer Column Definitions
// ---------------------------------------------------------------------------
// Returns TanStack Table ColumnDef arrays for each data source.
// Each call returns a NEW array to guarantee immutability.
// ---------------------------------------------------------------------------

import React from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import type {
  DataSourceId,
  ProfileRow,
  ContentRow,
  CommentRow,
  BrandRow,
  HashtagRow,
  TeamRow,
  ChannelRow,
  PlatformId,
} from '@/components/v11/discovery/types'
import { buildColumnsForSource } from './columnFactory'
import {
  COLUMNS_COMMENTS,
  COLUMNS_BRANDS,
  COLUMNS_TEAMS,
  COLUMNS_CONTENT,
  COLUMNS_HASHTAGS,
  COLUMNS_PROFILES,
  COLUMNS_CHANNELS,
} from '@/components/v11/discovery/constants'

// ---------------------------------------------------------------------------
// Column builders per data source
// ---------------------------------------------------------------------------

function buildProfileColumns(): ColumnDef<ProfileRow>[] {
  return buildColumnsForSource<ProfileRow & Record<string, unknown>>(COLUMNS_PROFILES) as ColumnDef<ProfileRow>[]
}

function buildContentColumns(): ColumnDef<ContentRow>[] {
  return buildColumnsForSource<ContentRow & Record<string, unknown>>(COLUMNS_CONTENT) as ColumnDef<ContentRow>[]
}

function buildCommentColumns(): ColumnDef<CommentRow>[] {
  return buildColumnsForSource<CommentRow & Record<string, unknown>>(COLUMNS_COMMENTS) as ColumnDef<CommentRow>[]
}

function buildBrandColumns(): ColumnDef<BrandRow>[] {
  return buildColumnsForSource<BrandRow & Record<string, unknown>>(COLUMNS_BRANDS) as ColumnDef<BrandRow>[]
}

function buildHashtagColumns(): ColumnDef<HashtagRow>[] {
  return buildColumnsForSource<HashtagRow & Record<string, unknown>>(COLUMNS_HASHTAGS) as ColumnDef<HashtagRow>[]
}

function buildTeamColumns(): ColumnDef<TeamRow>[] {
  return buildColumnsForSource<TeamRow & Record<string, unknown>>(COLUMNS_TEAMS) as ColumnDef<TeamRow>[]
}

function buildChannelColumns(): ColumnDef<ChannelRow>[] {
  return buildColumnsForSource<ChannelRow>(COLUMNS_CHANNELS)
}

// ---------------------------------------------------------------------------
// Column builder lookup
// ---------------------------------------------------------------------------

const COLUMN_BUILDERS: Readonly<Record<DataSourceId, () => ColumnDef<unknown>[]>> = {
  profiles: buildProfileColumns as () => ColumnDef<unknown>[],
  content: buildContentColumns as () => ColumnDef<unknown>[],
  comments: buildCommentColumns as () => ColumnDef<unknown>[],
  brands: buildBrandColumns as () => ColumnDef<unknown>[],
  hashtags: buildHashtagColumns as () => ColumnDef<unknown>[],
  teams: buildTeamColumns as () => ColumnDef<unknown>[],
  channels: buildChannelColumns as () => ColumnDef<unknown>[],
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns a new array of ColumnDef for the given data source.
 * Each call produces a fresh array (immutable pattern).
 */
export function getColumnsForSource(source: DataSourceId): ColumnDef<unknown>[] {
  const builder = COLUMN_BUILDERS[source]
  return builder()
}
